import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HelpCircle, BookOpen, ChevronRight, X, ExternalLink } from 'lucide-react';
import { knowledgeApi } from '../services/api';
import helpMapping from './helpMapping';
import './HelpButton.css';

// inline — кнопка внутри верхней панели телефона/ТСД (Layout, .mobile-topbar),
// а не плавающая поверх страницы (аудит №3, U6)
export default function HelpButton({ inline = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);

  // Найти маппинг для текущего пути
  const currentPath = Object.keys(helpMapping).find(path =>
    location.pathname === path || location.pathname.startsWith(path + '/')
  );
  const mapping = currentPath ? helpMapping[currentPath] : null;

  useEffect(() => {
    if (!open || !mapping) return;
    setLoading(true);
    const slugs = mapping.slugs.join(',');
    knowledgeApi.getArticles({ slugs, per_page: 10 }).then(res => {
      setArticles(res.data?.items || []);
    }).catch(() => {
      setArticles([]);
    }).finally(() => setLoading(false));
  }, [open, mapping]);

  // Скрыть на страницах KB
  if (location.pathname.startsWith('/knowledge') || location.pathname.startsWith('/admin/knowledge')) {
    return null;
  }

  // Не показывать если нет маппинга
  if (!mapping) return null;

  return (
    <>
      <button
        type="button"
        className={inline ? 'help-btn help-btn--inline' : 'help-btn'}
        aria-label="Справка по разделу"
        onClick={() => setOpen(true)}
        title={`Справка: ${mapping.title}`}
      >
        <HelpCircle size={18} />
      </button>

      {open && (
        <div className="help-overlay" onClick={() => setOpen(false)}>
          <aside className="help-drawer" onClick={e => e.stopPropagation()}>
            <div className="help-drawer__header">
              <div className="help-drawer__header-content">
                <BookOpen size={18} />
                <h3>Справка: {mapping.title}</h3>
              </div>
              <button className="help-drawer__close" onClick={() => setOpen(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="help-drawer__body">
              {loading ? (
                <div className="help-drawer__loading">Загрузка...</div>
              ) : articles.length === 0 ? (
                <div className="help-drawer__empty">
                  <p>Статьи для этого раздела пока не добавлены.</p>
                  <button
                    className="help-drawer__link"
                    onClick={() => { setOpen(false); navigate('/knowledge'); }}
                  >
                    Открыть базу знаний <ExternalLink size={14} />
                  </button>
                </div>
              ) : (
                <div className="help-drawer__list">
                  {articles.map(a => (
                    <button
                      key={a.slug}
                      className="help-drawer__item"
                      onClick={() => {
                        setOpen(false);
                        navigate(`/knowledge/${a.category}/${a.slug}`);
                      }}
                    >
                      <div className="help-drawer__item-content">
                        <span className="help-drawer__item-title">{a.title}</span>
                        {a.summary && (
                          <span className="help-drawer__item-summary">{a.summary}</span>
                        )}
                      </div>
                      <ChevronRight size={14} className="help-drawer__item-arrow" />
                    </button>
                  ))}
                </div>
              )}

              <div className="help-drawer__footer">
                <button
                  className="help-drawer__link"
                  onClick={() => { setOpen(false); navigate('/knowledge'); }}
                >
                  Все статьи базы знаний <ExternalLink size={14} />
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
